import { BookOpenIcon, CalendarPlusIcon, HelpIcon, TranslateIcon, VideoRecorderIcon, VoiceIcon } from '@braskai/icons';

import type { PageViewSourceState } from '@/shared/lib/pageViewSource';
import type { NavigationItemProps } from '@/shared/ui/NavigationItem';

import { RECORDING_ANNOUNCEMENT_HOME_ANCHOR } from '@/features/recordingAnnouncement';

import { HELP_CENTER_LINK, RASK_BOOK_A_CALL_WITH_CS_LINK, WHITE_LABEL_SUPPORT_LINK } from '@/shared/constants/docLinks';
import { PATHS } from '@/shared/constants/routes/paths';
import { translate, TranslationKey } from '@/shared/i18n';
import { ChatIcon, showChat } from '@/shared/thirdParty/hubSpot';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PlanCategory } from '@/shared/types/Billing';

import { HomeIcon } from '../ui/icons';

type Translate = (key: TranslationKey) => string;

type GetNavOptionsProps = {
  isRecordingVisible?: boolean;
  pathname: string;
  spaceId: string;
  t?: Translate;
  onNewRecording?: () => void;
  onNewTranslation?: () => void;
};

type GetToolsNavOptionsProps = {
  hasTrialPlan?: boolean;
  isGlossaryFeatureVisible: boolean;
  isMobile?: boolean;
  isVoicePresetsFeatureVisible: boolean;
  pathname: string;
  planCategory?: PlanCategory;
  spaceId: string;
  t?: Translate;
};

const defaultTranslate: Translate = (key) => translate('en', key);

const SIDEBAR_SOURCE_STATE: PageViewSourceState = { source: 'sidebar' };

const isEnterpriseOrBusinessPlan = (planCategory?: PlanCategory) =>
  planCategory === PlanCategory.Enterprise || planCategory === PlanCategory.Business;

const getBookACallWithCSNavItem = (t: Translate): NavigationItemProps => ({
  title: t('sidebar.bookCall'),
  dataTestId: 'sidebar-schedule-call-CS',
  icon: <CalendarPlusIcon height={16} width={16} />,
  onClick: () => {
    window.open(RASK_BOOK_A_CALL_WITH_CS_LINK, '_blank', 'noopener');
    MixpanelEvents.scheduleCallCSClick();
  },
});

export const getSpacePath = (path: string, spaceId?: string) => {
  if (!spaceId) return path;

  if (path === PATHS.homepage) {
    return `/spaces/${spaceId}`;
  }

  return `/spaces/${spaceId}${path.startsWith('/') ? path : `/${path}`}`;
};

const getSpaceSection = (pathname: string) => {
  const [, spaces, spaceId, section = ''] = pathname.split('/');

  if (spaces !== PATHS.spaces || !spaceId) return null;

  return section;
};

const isSectionSelected = (pathname: string, sections: string[]) => {
  const section = getSpaceSection(pathname);

  return section !== null && sections.includes(section);
};

export const getNavOptions = ({
  isRecordingVisible = true,
  pathname,
  spaceId,
  t = defaultTranslate,
  onNewRecording,
  onNewTranslation,
}: GetNavOptionsProps): NavigationItemProps[] => {
  const navItems: NavigationItemProps[] = [
    {
      title: t('sidebarNav.home'),
      dataTestId: 'sidebar-menu-home',
      icon: <HomeIcon height={16} width={16} />,
      navigationState: SIDEBAR_SOURCE_STATE,
      path: getSpacePath(PATHS.homepage, spaceId),
      selected: isSectionSelected(pathname, ['']),
      spotlightAnchor: RECORDING_ANNOUNCEMENT_HOME_ANCHOR,
    },
    {
      title: t('sidebar.translation'),
      action: onNewTranslation && {
        title: t('sidebarNav.newTranslation'),
        dataTestId: 'sidebar-menu-translation-new',
        onClick: onNewTranslation,
      },
      dataTestId: 'sidebar-menu-translation',
      icon: <TranslateIcon height={16} width={16} />,
      navigationState: SIDEBAR_SOURCE_STATE,
      path: getSpacePath(PATHS.translation, spaceId),
      selected: isSectionSelected(pathname, [PATHS.translation, PATHS.folders]),
      onClick: () => {
        MixpanelEvents.translationTabClick();
      },
    },
  ];

  if (isRecordingVisible) {
    navItems.push({
      title: t('sidebar.recordVideo'),
      action: onNewRecording && {
        title: t('sidebarNav.newRecording'),
        dataTestId: 'sidebar-menu-records-new',
        onClick: onNewRecording,
      },
      dataTestId: 'sidebar-menu-records',
      icon: <VideoRecorderIcon height={16} width={16} />,
      navigationState: SIDEBAR_SOURCE_STATE,
      path: getSpacePath(PATHS.records, spaceId),
      selected: isSectionSelected(pathname, [PATHS.records]),
      onClick: () => {
        MixpanelEvents.recordingTabClick();
      },
    });
  }

  return navItems;
};

export const getToolsNavOptions = ({
  hasTrialPlan = false,
  isGlossaryFeatureVisible,
  isMobile = false,
  isVoicePresetsFeatureVisible,
  pathname,
  planCategory,
  spaceId,
  t = defaultTranslate,
}: GetToolsNavOptionsProps): NavigationItemProps[] => {
  const navItems: NavigationItemProps[] = [];

  if (isVoicePresetsFeatureVisible) {
    navItems.push({
      title: t('sidebar.voiceCloneLibrary'),
      dataTestId: 'sidebar-menu-voice-clone',
      icon: <VoiceIcon height={16} width={16} />,
      path: getSpacePath(PATHS.voices, spaceId),
      selected: isSectionSelected(pathname, [PATHS.voices]),
      onClick: () => {
        MixpanelEvents.voiceCloneLibraryTabClick();
      },
    });
  }

  if (isGlossaryFeatureVisible) {
    navItems.push({
      title: t('sidebar.dictionary'),
      dataTestId: 'sidebar-menu-dictionary',
      icon: <BookOpenIcon height={16} width={16} />,
      isDisabled: isMobile,
      path: getSpacePath(PATHS.glossaries, spaceId),
      rightText: isMobile ? t('common.desktopOnly') : undefined,
      selected: isSectionSelected(pathname, [PATHS.glossaries]),
      onClick: () => {
        MixpanelEvents.dictionaryTabClick();
      },
    });
  }

  // Trial users go through the new Book-a-Demo flow — hide the CS link to avoid double CTAs.
  if (isMobile && isEnterpriseOrBusinessPlan(planCategory) && !hasTrialPlan) {
    navItems.push(getBookACallWithCSNavItem(t));
  }

  return navItems;
};

type GetHelpNavOptionsProps = {
  hasTrialPlan?: boolean;
  isMobile?: boolean;
  isWhiteLabel?: boolean;
  planCategory?: PlanCategory;
  t?: Translate;
  settingsItem?: NavigationItemProps;
};

export const getHelpNavOptions = ({
  hasTrialPlan = false,
  isMobile = false,
  isWhiteLabel = false,
  planCategory,
  t = defaultTranslate,
  settingsItem,
}: GetHelpNavOptionsProps): NavigationItemProps[] => {
  const desktopSettingsItems = !isMobile && settingsItem ? [settingsItem] : [];

  if (isWhiteLabel) {
    return [
      ...desktopSettingsItems,
      {
        title: t('sidebar.getHelp'),
        dataTestId: 'sidebar-question-button',
        icon: <HelpIcon height={16} width={16} />,
        onClick: () => {
          window.open(`mailto:${WHITE_LABEL_SUPPORT_LINK}`);
        },
      },
    ];
  }

  const helpCenterItem: NavigationItemProps = {
    title: t('sidebar.helpCenter'),
    dataTestId: 'sidebar-help-center',
    icon: <HelpIcon height={16} width={16} />,
    onClick: () => {
      MixpanelEvents.helpCenterClick();
      window.open(HELP_CENTER_LINK);
    },
  };

  const getHelpItem: NavigationItemProps = {
    title: t('sidebar.getHelp'),
    dataTestId: 'sidebar-question-button',
    icon: <ChatIcon height={16} width={16} />,
    onClick: () => {
      MixpanelEvents.getHelpClick();
      showChat();
    },
  };

  if (isMobile) {
    return [helpCenterItem, getHelpItem];
  }

  // Trial users go through the new Book-a-Demo flow — hide the CS link to avoid double CTAs.
  const bookACallItems =
    isEnterpriseOrBusinessPlan(planCategory) && !hasTrialPlan ? [getBookACallWithCSNavItem(t)] : [];

  return [...bookACallItems, ...desktopSettingsItems, helpCenterItem, getHelpItem];
};

export const getPromoNavOptions = (showViewPlans: boolean, t: Translate = defaultTranslate): NavigationItemProps[] => {
  if (!showViewPlans) return [];

  return [
    {
      title: t('sidebar.viewPlans'),
      dataTestId: 'sidebar-view-plans',
      isOutlined: true,
      navigationState: { pricingEntryPoint: 'sidebar_view_plans' },
      path: `/${PATHS.pricing}`,
      onClick: () => {
        MixpanelEvents.viewPlansSidebarClick();
      },
    },
  ];
};
